/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from 'react';

// Types
export interface SelectedEntity {
  cloud: string;
  account_id: string;
  account_display_name: string;
  region: string;
  type: 'reflector' | 'cluster';
  id: string;
  display_name: string;
  subId?: string; // Add subId for reflectors (from sidebar response)
}

/**
 * An AWS resource tag as the visibility document reports it — `{ name, value }`, not the Cloud
 * Propagation form's `{ tagName, tagValue }`. Kept separate on purpose: this is the wire shape.
 */
export interface CloudResourceTag {
  name: string;
  value: string;
}

/**
 * Why a destination was in a Cloud Propagation Rule's scope, normalized from the two wire
 * shapes the visibility document uses:
 *
 *   OUTBOUND — `tag_attribution: { source, tags: [...] }` on the prefix record
 *   INBOUND  — `targets[].route_table_tag: { name, value }`, one tag, no source
 *
 * `source` mirrors the rule's selection mode (`routing_table`, `subnet_tags`, `manual`) and is
 * null when the shape doesn't carry one — absence is not the same as "manual".
 */
export interface TagAttribution {
  source: string | null;
  tags: CloudResourceTag[];
}

/**
 * The address families the visibility UI models. Every wire spelling maps onto one of them.
 */
export type RouteFamily = 'ipv4-unicast' | 'ipv6-unicast' | 'vpnv4';

/**
 * Maps the wire `family` string onto the family the UI models.
 *
 * The producers do not agree on one spelling: a VPNv4 route arrives as `vpnv4` from some and as
 * the RFC-flavoured `ipv4-mpls-vpn` from others, while the same document's
 * `statistics.family_distribution` keys that very same route as `vpnv4_cpr` (CTX-338). So the
 * rule is by meaning, not by an allow-list: anything naming VPN or MPLS is VPNv4, anything
 * else naming IPv6 is IPv6 unicast (CTX-759), and everything else — including an absent
 * family — is IPv4 unicast, which is what each call site defaulted to before this helper
 * existed. `RouteFilterToolbar` classifies the filter options the same way.
 *
 * Deliberately total rather than returning `null` for the unknown case: every route belongs to
 * one of the buckets, and an unmatched family is what silently emptied the topology in the
 * first place. An unrecognised value lands in IPv4 unicast, where it stays visible and
 * filterable instead of vanishing.
 */
export function normalizeRouteFamily(raw: unknown): RouteFamily {
  if (typeof raw !== 'string') {
    return 'ipv4-unicast';
  }
  const value = raw.trim().toLowerCase();
  if (value.includes('vpn') || value.includes('mpls')) {
    return 'vpnv4';
  }
  return value.includes('ipv6') ? 'ipv6-unicast' : 'ipv4-unicast';
}

const ROUTE_FAMILY_LABELS: Record<RouteFamily, string> = {
  'ipv4-unicast': 'IPv4U',
  'ipv6-unicast': 'IPv6U',
  vpnv4: 'VPNv4'
};

/**
 * The family badge text, for any wire or normalized spelling. The one place the label is
 * decided, so the table and every modal read the same family the same way.
 */
export function routeFamilyLabel(raw: unknown): string {
  return ROUTE_FAMILY_LABELS[normalizeRouteFamily(raw)];
}

/**
 * Whether a family rides the unicast bucket. naboo files IPv6 unicast under the same
 * `ipv4_unicast_cpr` key as IPv4 unicast, so that key means "unicast", not "IPv4".
 */
export function isUnicastFamily(family: RouteFamily): boolean {
  return family !== 'vpnv4';
}

export interface CprEntry {
  prefix: string;
  vpc: string;
  family: RouteFamily;
  route_table_id: string;
  // Wire values are 'upsert' | 'delete'; the transform normalizes to 'permit' | 'deny'.
  // A withdraw tombstone is 'delete' (or decision_reason='delete_explicit_withdraw').
  action: 'permit' | 'deny' | 'upsert' | 'delete';
  neighbor_ip: string;
  next_hop_type: string;
  specific_next_hop: string;
  local_pref: number;
  med: number;
  as_path: number[];
  origin: string;
  rd: string | null;
  rt: string | null;
  candidates: Array<{
    neighbor_ip: string;
    local_pref: number;
    med: number;
    as_path: number[];
    origin: string;
    selected: boolean;
  }>;
  decision_reason: string;
  timestamp: number;
  __source: string;
  // Best path indicator (optional - used for new INBOUND structure)
  selected?: boolean;
  // Outbound-specific fields (optional for backward compatibility)
  subnet_id?: string;
  rule_id?: number;
  status?: string;
  extensions?: {
    community?: string[];
  };
  // Withdraw tombstone (action='delete'): vpc_id is always null on the wire,
  // so route identity for these entries comes from route_table_id + withdrawn_neighbor_ip.
  withdrawn?: boolean;
  // Wire fields from the visibility payload's target/prefix records (CTX-12 scope C).
  // Both are commonly present only on withdraw tombstones; null otherwise.
  withdrawn_neighbor_ip: string | null;
  received_at: string | null;
  /**
   * The AWS tag(s) that made this destination match a tag-based Cloud Propagation Rule, plus
   * how it was selected (CTX-129). Normalized by `normalizeTagAttribution` from whichever wire
   * shape the direction uses — see `TagAttribution`.
   *
   * `null` means the payload carried no attribution at all: either the destination was pinned
   * explicitly, or the reflector document predates the field. Those are indistinguishable
   * unless `source` says otherwise, which is why the UI shows nothing rather than "manual".
   */
  tag_attribution?: TagAttribution | null;
  /**
   * What the reflector achieved in AWS for this (prefix, route table), as
   * opposed to what BGP decided (CTX-323). `injection_*` is the injector's own
   * write result; `aws_*` is what the reconciliation sweep last observed in
   * AWS. Both are absent on reflectors that predate the feature — see
   * `deriveCloudRouteHealth`, which treats that as "no data", never as health.
   */
  /**
   * `not_injected` is a display-only row naboo adds for a route table nothing was ever written
   * to because the rule's next hop does not resolve to an ENI (CTX-738).
   */
  injection_status?: 'injected' | 'failed' | 'withdrawn' | 'not_injected' | null;
  injection_error?: string | null;
  injection_at?: number | null;
  aws_state?: 'active' | 'blackhole' | 'missing' | 'drift' | 'table_missing' | null;
  aws_checked_at?: number | null;
}

/**
 * A cluster/reflector member entry from `/v2/reflector/visibility` (CTX-25 contract).
 * `id` is the reflector subId — the stable identity to match/key by (never `name`).
 * `data` is `null` when the reflector has no S3 document yet (down / no data), not an error.
 */
export interface VisibilityMemberResponse {
  id: string;
  name: string;
  role: 'PRIMARY' | 'SECONDARY' | null;
  status: 'online' | 'offline' | 'unknown';
  lastUpdate: string | null;
  data: any | null;
}

export interface EntityStatusInfo {
  id: string;
  name: string;
  role: 'PRIMARY' | 'SECONDARY' | null;
  status: 'online' | 'offline' | 'unknown';
  lastUpdate: string | null;
  hasData: boolean;
}

// Outbound API response structure
export interface OutboundVpcData {
  vpc_id: string;
  vpc_name: string | null;
  total_subnets: number;
  total_announcements: number;
  subnets: Array<{
    subnet_id: string;
    cidr: string;
    rule_id: number;
    announcements: Array<{
      neighbor_ip: string;
      neighbor_as: number | null;
      next_hop: string;
      family: string;
      status: string;
      local_pref: number;
      origin: string;
      extensions: {
        community?: string[];
      };
    }>;
  }>;
}

export interface OutboundNeighborSummary {
  neighbor_ip: string;
  neighbor_as: number | null;
  session_state: string;
  total_prefixes_sent: number;
  prefixes: Array<{
    cidr: string;
    subnet_id: string;
    vpc_id: string;
    next_hop: string;
    local_pref: number;
    origin: string;
    rule_id: number;
    status: string;
    extensions: {
      community?: string[];
    };
  }>;
}

export interface OutboundData {
  vpcs: OutboundVpcData[];
  neighbors_summary: OutboundNeighborSummary[];
  statistics: {
    total_vpcs: number;
    total_subnets: number;
    total_neighbors: number;
    total_announcements: number;
    family_distribution: Record<string, number>;
    status_distribution: Record<string, number>;
  };
  timestamp: number;
  __source: string;
}

export interface CPR {
  entity: { id: string; type: string };
  filters: { prefix: string | null; family: string | null };
  INBOUND: {
    ipv4_unicast_cpr: CprEntry[];
    vpnv4_cpr: CprEntry[];
  };
  OUTBOUND: {
    ipv4_unicast_cpr: CprEntry[];
    // Raw outbound data structure (for reference)
    rawOutboundData?: OutboundData;
  };
}

export interface CloudInventory {
  clouds: Array<{
    name: string;
    id: string;
    accounts: Array<{
      id: string;
      name: string;
      regions: Array<{
        id: string;
        name: string;
        reflectors: Array<{
          type: 'reflector';
          id: string;
          display_name: string;
          subId?: string; // Add subId for reflectors
          status: 'online' | 'offline' | 'degraded';
          role: string;
          cloud: string;
          region: string;
          version: string;
          vpc: string;
          tag: string;
          last_sync: string;
          creation_date: string;
          license_type: string;
          license_serial: string;
          iam_permissions: any;
        }>;
        clusters: Array<{
          type: 'cluster';
          id: string;
          display_name: string;
          subId?: string; // Add subId for clusters
          status: 'online' | 'offline' | 'degraded';
          health: 'Healthy' | 'Degraded' | 'Offline';
          reflectors: any[];
        }>;
      }>;
    }>;
  }>;
}

export interface BgpNeighborData {
  neighborIp: string;
  prefixes: Array<{
    prefix: string;
    family: string;
    local_pref: number;
    med: number;
    as_path: number[];
    vpcCount: number;
    rtCount: number;
  }>;
}

/** One peer as `/v2/reflector/visibility/neighbordata` reports it. */
export interface BgpNeighborRosterEntry {
  peerAs?: number;
  description?: string;
  operatingMode?: string;
  status?: string;
  statusChangedAt?: number;
}

/**
 * The reflector's configured BGP peers with their live session state, keyed by neighbor IP.
 *
 * This roster — NOT the route payload — is the source of truth for *who* the reflector peers
 * with. A peer whose session is down exchanges no prefixes, so it appears nowhere in the CPR
 * data: anything derived from routes alone cannot tell a down peer from one that was never
 * configured. Surfaces that must account for every peer read this.
 */
export type BgpNeighborRoster = Record<string, BgpNeighborRosterEntry>;

export interface VpcData {
  vpcId: string;
  routeTables: Array<{
    route_table_id: string;
    prefixes: CprEntry[];
  }>;
}

export interface CloudData {
  label: string;
  vpcs: string[];
  allData: CprEntry[];
  accountId?: string;
  region?: string;
  headerColor?: string;
}

// Hook for BGP Visibility Data Management
export const useBgpVisibilityData = () => {

  // Map SSE data to CloudInventory format
  const mapSSEDataToCloudInventory = useCallback((sseData: any): CloudInventory | null => {
    if (!sseData || !sseData.clouds) {
      return null;
    }

    return {
      clouds: sseData.clouds.map((cloud: any) => ({
        name: cloud.name,
        id: cloud.name.toLowerCase(),
        accounts: cloud.accounts.map((account: any) => ({
          id: account.id,
          name: account.display_name,
          regions: account.regions.map((region: any) => ({
            id: region.name,
            name: region.name,
            reflectors: (region.reflectors || []).map((reflector: any) => ({
              type: "reflector" as const,
              id: reflector.id,
              display_name: reflector.display_name,
              subId: reflector.subId || reflector.sub_id, // Extract subId from sidebar response
              status: reflector.status as "online" | "offline" | "degraded",
              role: reflector.role,
              cloud: cloud.name,
              region: region.name,
              version: reflector.version,
              vpc: reflector.vpc,
              tag: reflector.tag,
              last_sync: reflector.last_sync,
              creation_date: reflector.creation_date,
              license_type: reflector.license_type,
              license_serial: reflector.license_serial,
              iam_permissions: reflector.iam_permissions
            })),
            clusters: (region.clusters || []).map((cluster: any) => {
              const mapped = {
                type: "cluster" as const,
                id: cluster.id,
                display_name: cluster.display_name,
                subId: cluster.subId || cluster.sub_id, // Extract subId from sidebar response
                status: cluster.status as "online" | "offline" | "degraded",
                health: cluster.health as "Healthy" | "Degraded" | "Offline",
                reflectors: cluster.reflectors || []
              };
              return mapped;
            })
          }))
        }))
      }))
    };
  }, []);


  // Process BGP data for display
  const processBgpData = useCallback((cprData: any, direction: 'INBOUND' | 'OUTBOUND', showIPv4U: boolean, showVPNv4: boolean) => {
    if (!cprData) {
      return {
        neighbors: [],
        vpcs: [],
        cloudData: null,
        metrics: {
          neighbors: 0,
          vpcs: 0,
          rts: 0,
          prefixes: 0
        }
      };
    }

    // Filter data based on direction and family
    const set = direction === 'INBOUND' ? cprData.INBOUND : cprData.OUTBOUND;
    const rows = [
      ...(showIPv4U ? (set.ipv4_unicast_cpr || []) : []),
      ...(showVPNv4 && direction === 'INBOUND' ? ((set as any).vpnv4_cpr || []) : []),
    ];

    // Group by neighbor and collect prefixes
    const neighborPrefixes = new Map<string, any[]>();
    
    // Get all unique peers
    const allPeers = new Set<string>();
    rows.forEach(record => {
      allPeers.add(record.neighbor_ip);
      if (record.candidates) {
        record.candidates.forEach((candidate: any) => {
          allPeers.add(candidate.neighbor_ip);
        });
      }
    });
    
    // For each peer, find all prefixes where it appears
    Array.from(allPeers).forEach(peer => {
      if (!neighborPrefixes.has(peer)) {
        neighborPrefixes.set(peer, []);
      }
      
      rows.forEach(record => {
        const { prefix, family } = record;
        const fam = (family || 'ipv4-unicast').toLowerCase();
        
        // Check if this peer already has this prefix
        const peerPrefixes = neighborPrefixes.get(peer);
        const existingPrefix = peerPrefixes?.find(p => 
          p.prefix === prefix && p.family === fam
        );
        
        if (!existingPrefix) {
          let peerData = null;
          
          // If it's the main peer of the record
          if (record.neighbor_ip === peer) {
            peerData = {
              local_pref: record.local_pref,
              med: record.med,
              as_path: record.as_path
            };
          }
          // If it's a candidate
          else if (record.candidates) {
            const candidateData = record.candidates.find((c: any) => c.neighbor_ip === peer);
            if (candidateData) {
              peerData = {
                local_pref: candidateData.local_pref,
                med: candidateData.med,
                as_path: candidateData.as_path
              };
            }
          }
          
          // Only add if we found data for this peer
          if (peerData) {
            // Count unique VPCs and RTs for this prefix from this peer
            const relatedRecords = rows.filter(r => 
              r.prefix === prefix && 
              (r.neighbor_ip === peer || r.candidates?.some((c: any) => c.neighbor_ip === peer))
            );
            
            const vpcCount = new Set(relatedRecords.map(r => r.vpc)).size;
            const rtCount = relatedRecords.length;
            
            const peerPrefixes = neighborPrefixes.get(peer);
            if (peerPrefixes) {
              peerPrefixes.push({
                neighbor_ip: peer,
                prefix: prefix,
                family: fam,
                local_pref: peerData.local_pref || 0,
                med: peerData.med || 0,
                as_path: peerData.as_path || [],
                vpcCount: vpcCount,
                rtCount: rtCount
              });
            }
          }
        }
      });
    });

    // Create neighbor data
    const neighbors: BgpNeighborData[] = Array.from(neighborPrefixes.entries()).map(([neighborIp, prefixes]) => ({
      neighborIp,
      prefixes
    }));

    // Create VPC data
    const uniqueVPCs = Array.from(new Set(rows.map(entry => entry.vpc)));
    const vpcs: VpcData[] = uniqueVPCs.map(vpc => {
      const routeTables = rows
        .filter(entry => entry.vpc === vpc)
        .reduce((acc: any, entry) => {
          const existing = acc.find((rt: any) => rt.route_table_id === entry.route_table_id);
          if (existing) {
            existing.prefixes.push(entry);
          } else {
            acc.push({
              route_table_id: entry.route_table_id,
              prefixes: [entry]
            });
          }
          return acc;
        }, []);

      return {
        vpcId: vpc,
        routeTables
      };
    });

    // Create cloud data
    const cloudData: CloudData = {
      label: 'Cloud (AWS)',
      vpcs: uniqueVPCs,
      allData: rows
    };

    // Calculate metrics
    const metrics = {
      neighbors: neighbors.length,
      vpcs: uniqueVPCs.length,
      rts: Array.from(new Set(rows.map(entry => entry.route_table_id))).length,
      prefixes: Array.from(new Set(rows.map(entry => `${entry.prefix}|${entry.family}`))).length
    };

    return {
      neighbors,
      vpcs,
      cloudData,
      metrics
    };
  }, []);

  return {
    mapSSEDataToCloudInventory,
    processBgpData
  };
};

// Utility functions for filtering and searching
export const useCloudInventoryFiltering = (cloudInventory: CloudInventory | null) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const filteredCloudInventory = useMemo(() => {
    if (!cloudInventory || !searchTerm) return cloudInventory;

    const searchLower = searchTerm.toLowerCase();
    
    return {
      ...cloudInventory,
      clouds: cloudInventory.clouds.map(cloud => ({
        ...cloud,
        accounts: cloud.accounts.map(account => ({
          ...account,
          regions: account.regions.map(region => ({
            ...region,
            reflectors: region.reflectors.filter(reflector => 
              reflector.display_name.toLowerCase().includes(searchLower) ||
              reflector.id.toLowerCase().includes(searchLower) ||
              reflector.vpc.toLowerCase().includes(searchLower)
            ),
            clusters: region.clusters.filter(cluster =>
              cluster.display_name.toLowerCase().includes(searchLower) ||
              cluster.id.toLowerCase().includes(searchLower)
            )
          })).filter(region => 
            region.reflectors.length > 0 || region.clusters.length > 0
          )
        })).filter(account => 
          account.regions.length > 0
        )
      })).filter(cloud => 
        cloud.accounts.length > 0
      )
    };
  }, [cloudInventory, searchTerm]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return {
    searchTerm,
    setSearchTerm,
    expandedSections,
    toggleSection,
    filteredCloudInventory
  };
};

export default useBgpVisibilityData;
